import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { HashLink } from 'react-router-hash-link';
import { FaSearch, FaTimesCircle } from 'react-icons/fa';
import {
  FiSettings, FiEdit2, FiBookOpen, FiChevronRight, FiChevronDown,
  FiArrowLeft, FiArrowRight, FiMenu, FiX, FiHelpCircle, FiBarChart2,
} from 'react-icons/fi';
import LoadingSpinner      from 'components/common/LoadingSpinner';
import Button              from 'components/common/Button';
import UserGuideAdminPanel from 'components/app/documentation/UserGuideAdminPanel';
import DocViewStatsPanel   from 'components/app/documentation/DocViewStatsPanel';
import { fetchPublishedGuides, fetchUserGuides } from 'redux/documentation/userGuideSlice';
import { recordDocView } from 'redux/documentation/docViewSlice';
import { contentImageCss } from 'components/common/rich-text/richTextContentCss';
import { processAnchors } from 'helpers/guideAnchors';
import { debounce } from 'helpers/debounce';
import createMarkup from 'helpers/createMarkup';
import PropTypes from 'prop-types';

const SEP = ' / ';
const extractParent = (g) => {
  const i = (g || '').indexOf(SEP);
  return i >= 0 ? g.slice(0, i) : (g || '');
};
const extractSub = (g) => {
  const i = (g || '').indexOf(SEP);
  return i >= 0 ? g.slice(i + SEP.length) : '';
};

// Strip emojis/pictographs from user-entered titles so the UI stays clean.
const EMOJI_RE = /(?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2300}-\u{23FF}\u{1F000}-\u{1F2FF}]|\u{FE0F}|\u{200D})/gu;
const stripEmoji = (s) => (s || '').replace(EMOJI_RE, '').replace(/\s+/g, ' ').trim();

// Per-group color palette. Each parent gets a consistent color (hashed by name).
// All cool tones so they harmonise with the cool-grey page background.
// All classnames are written out as literals so Tailwind JIT picks them up.
const GROUP_PALETTE = [
  { dot: 'tw-bg-primary-500',  tint: 'tw-bg-primary-100/40',  text: 'tw-text-primary-700',  bar: 'tw-border-primary-400',  countBg: 'tw-bg-primary-100 tw-text-primary-700'  },
  { dot: 'tw-bg-blue-500',    tint: 'tw-bg-blue-100/40',    text: 'tw-text-blue-700',    bar: 'tw-border-blue-400',    countBg: 'tw-bg-blue-100 tw-text-blue-700'    },
  { dot: 'tw-bg-sky-500',     tint: 'tw-bg-sky-100/40',     text: 'tw-text-sky-700',     bar: 'tw-border-sky-400',     countBg: 'tw-bg-sky-100 tw-text-sky-700'     },
  { dot: 'tw-bg-cyan-500',    tint: 'tw-bg-cyan-100/40',    text: 'tw-text-cyan-700',    bar: 'tw-border-cyan-400',    countBg: 'tw-bg-cyan-100 tw-text-cyan-700'    },
  { dot: 'tw-bg-teal-500',    tint: 'tw-bg-teal-100/40',    text: 'tw-text-teal-700',    bar: 'tw-border-teal-400',    countBg: 'tw-bg-teal-100 tw-text-teal-700'    },
  { dot: 'tw-bg-emerald-500', tint: 'tw-bg-emerald-100/40', text: 'tw-text-emerald-700', bar: 'tw-border-emerald-400', countBg: 'tw-bg-emerald-100 tw-text-emerald-700' },
  { dot: 'tw-bg-violet-500',  tint: 'tw-bg-violet-100/40',  text: 'tw-text-violet-700',  bar: 'tw-border-violet-400',  countBg: 'tw-bg-violet-100 tw-text-violet-700'  },
  { dot: 'tw-bg-rose-500',    tint: 'tw-bg-rose-100/40',    text: 'tw-text-rose-700',    bar: 'tw-border-rose-400',    countBg: 'tw-bg-rose-100 tw-text-rose-700'    },
];

const colorForGroup = (name) => {
  const s = name || '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return GROUP_PALETTE[Math.abs(h) % GROUP_PALETTE.length];
};

// ── Sticky header ─────────────────────────────────────────────────────────────
const StickyHeader = ({
  search, onSearchChange, onClearSearch,
  isAdmin, showAdmin, onToggleAdmin,
  showStats, onToggleStats,
  selected, onToggleMobileNav,
}) => (
  <header
    className="tw-sticky tw-z-30 tw-bg-slate-50/90 tw-backdrop-blur-md tw-border-b tw-border-slate-200 tw-shadow-[0_1px_3px_rgba(15,23,42,0.06)]"
    style={{ top: 'var(--falcon-top-nav-height, 4.3125rem)' }}
  >
    <div className="tw-max-w-screen-2xl tw-mx-auto tw-px-5 sm:tw-px-8 tw-py-3 tw-flex tw-items-center tw-gap-3 tw-min-h-[3.75rem]">
      <button
        type="button"
        onClick={onToggleMobileNav}
        className="lg:tw-hidden tw-p-2 tw-text-slate-600 hover:tw-text-primary-700 hover:tw-bg-slate-200/60 tw-bg-transparent tw-border-0 tw-rounded-md tw-transition-colors"
        aria-label="Toggle navigation"
      >
        <FiMenu size={18} />
      </button>

      <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
        <div className="tw-p-1.5 tw-bg-gradient-to-br tw-from-primary-500 tw-to-blue-600 tw-rounded tw-shadow-sm">
          <FiBookOpen className="tw-text-primary-50" size={16} />
        </div>
        <span className="tw-font-bold tw-text-slate-800 tw-text-sm tw-hidden sm:tw-inline tw-tracking-tight">
          User Guide
        </span>
      </div>

      {selected && (
        <nav className="tw-hidden md:tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 tw-min-w-0 tw-ml-2">
          <FiChevronRight size={12} className="tw-shrink-0 tw-text-slate-400" />
          <span className="tw-truncate">{stripEmoji(extractParent(selected.group_name))}</span>
          {extractSub(selected.group_name) && (
            <>
              <FiChevronRight size={12} className="tw-shrink-0 tw-text-slate-400" />
              <span className="tw-truncate">{stripEmoji(extractSub(selected.group_name))}</span>
            </>
          )}
          <FiChevronRight size={12} className="tw-shrink-0 tw-text-slate-400" />
          <span className="tw-text-primary-700 tw-font-semibold tw-truncate">{stripEmoji(selected.title)}</span>
        </nav>
      )}

      <div className="tw-flex-1" />

      <div className="tw-relative tw-shrink-0 tw-group">
        <FaSearch
          className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-500 group-focus-within:tw-text-primary-600 tw-transition-colors tw-pointer-events-none"
          size={13}
        />
        <input
          id="user-guide-search"
          type="text"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search guides..."
          className="tw-w-44 sm:tw-w-72 lg:tw-w-80 tw-h-9 tw-pl-10 tw-pr-20 tw-text-sm tw-text-slate-800 placeholder:tw-text-slate-500 tw-border tw-border-slate-300 tw-rounded-xl tw-bg-slate-200/70 hover:tw-bg-slate-100 hover:tw-border-slate-400 focus:tw-bg-primary-50/50 focus:tw-border-primary-500 focus:tw-ring-4 focus:tw-ring-primary-500/15 focus:tw-w-56 sm:focus:tw-w-80 lg:focus:tw-w-96 tw-outline-none tw-transition-all tw-duration-200"
        />
        {search ? (
          <button
            type="button"
            onClick={onClearSearch}
            className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 hover:tw-text-rose-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-1 tw-rounded-xl tw-transition-colors"
            aria-label="Clear search"
          >
            <FaTimesCircle size={14} />
          </button>
        ) : (
          <kbd className="tw-hidden md:tw-inline-flex tw-items-center tw-gap-0.5 tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-[10px] tw-font-bold tw-text-primary-700 tw-bg-primary-100 tw-border tw-border-primary-200 tw-rounded-md tw-px-1.5 tw-py-0.5 tw-font-mono tw-pointer-events-none">
            Ctrl K
          </kbd>
        )}
      </div>

      {isAdmin && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <Button
            variant={showStats ? 'primary' : 'ghost'}
            size="sm"
            onClick={onToggleStats}
          >
            <FiBarChart2 size={13} />
            <span className="tw-hidden sm:tw-inline">{showStats ? 'Hide Stats' : 'Statistics'}</span>
          </Button>
          <Button
            variant={showAdmin ? 'primary' : 'ghost'}
            size="sm"
            onClick={onToggleAdmin}
          >
            <FiSettings size={13} />
            <span className="tw-hidden sm:tw-inline">{showAdmin ? 'Exit' : 'Manage'}</span>
          </Button>
        </div>
      )}
    </div>
  </header>
);
StickyHeader.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  showAdmin: PropTypes.bool.isRequired,
  onToggleAdmin: PropTypes.func.isRequired,
  showStats: PropTypes.bool,
  onToggleStats: PropTypes.func,
  selected: PropTypes.object,
  onToggleMobileNav: PropTypes.func.isRequired,
};

// ── Tree guide item ───────────────────────────────────────────────────────────
const TreeGuideItem = ({ guide, isActive, onClick, depth = 0 }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-w-full tw-text-left tw-py-1.5 tw-pr-3 tw-text-[13px] tw-leading-snug tw-transition-all tw-rounded-md tw-block tw-border-0 tw-relative ${
      isActive
        ? 'tw-bg-primary-100 tw-text-primary-800 tw-font-semibold tw-shadow-[inset_3px_0_0_0_theme(colors.indigo.600)]'
        : 'tw-text-slate-700 hover:tw-bg-slate-200/60 hover:tw-text-slate-900 tw-bg-transparent'
    }`}
    style={{ paddingLeft: `${0.75 + depth * 0.75}rem` }}
  >
    <span className="tw-block tw-truncate">{stripEmoji(guide.title)}</span>
  </button>
);
TreeGuideItem.propTypes = {
  guide: PropTypes.shape({ title: PropTypes.string.isRequired }).isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  depth: PropTypes.number,
};

// ── Collapsible group ─────────────────────────────────────────────────────────
const TreeGroup = ({ parent, subGroups, selectedSlug, onSelect, defaultOpen, isActiveGroup }) => {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  const totalCount = Object.values(subGroups).reduce((n, arr) => n + arr.length, 0);
  const c = colorForGroup(parent);

  return (
    <div className={`tw-mb-2 tw-rounded-lg tw-transition-colors ${isActiveGroup ? `${c.tint} tw-px-1.5 tw-py-1.5` : ''}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-bg-transparent tw-border-0 tw-rounded-sm tw-cursor-pointer tw-transition-colors ${
          isActiveGroup ? c.text : 'tw-text-slate-700 hover:tw-text-slate-900'
        }`}
      >
        {open
          ? <FiChevronDown size={11} className={isActiveGroup ? c.text : 'tw-text-slate-500'} />
          : <FiChevronRight size={11} className="tw-text-slate-400" />}
        <span className={`tw-w-2 tw-h-2 tw-rounded-xl tw-shrink-0 ${c.dot}`} />
        <span className="tw-flex-1 tw-text-left tw-truncate">{stripEmoji(parent)}</span>
        <span className={`tw-text-[10px] tw-font-bold tw-px-1.5 tw-rounded-sm tw-normal-case ${
          isActiveGroup ? c.countBg : 'tw-bg-slate-200/80 tw-text-slate-600'
        }`}>
          {totalCount}
        </span>
      </button>
      {open && (
        <div className={`tw-mt-1 tw-ml-3 tw-pl-2 tw-pr-1 tw-pb-1 ${isActiveGroup ? `tw-border-l-2 ${c.bar}` : 'tw-border-l tw-border-slate-200/80'}`}>
          {Object.entries(subGroups).map(([sub, items]) => (
            <div key={sub || '__top__'}>
              {sub && (
                <p className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-mt-1 tw-mb-0 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider ${
                  isActiveGroup ? c.text : 'tw-text-slate-500'
                }`}>
                  <FiChevronRight size={8} />{stripEmoji(sub)}
                </p>
              )}
              {items.map(g => (
                <TreeGuideItem
                  key={g.id}
                  guide={g}
                  isActive={selectedSlug === g.slug}
                  onClick={() => onSelect(g)}
                  depth={sub ? 1 : 0}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
TreeGroup.propTypes = {
  parent: PropTypes.string.isRequired,
  subGroups: PropTypes.object.isRequired,
  selectedSlug: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  defaultOpen: PropTypes.bool,
  isActiveGroup: PropTypes.bool,
};

// ── Right TOC sidebar (scroll-spy) ────────────────────────────────────────────
const TocSidebar = ({ toc, slug, activeId }) => {
  if (!toc.length) return null;
  return (
    <div className="tw-py-2">
      <p className="tw-text-[10px] tw-font-bold tw-text-primary-700 tw-uppercase tw-tracking-wider tw-px-3 tw-mb-2 tw-flex tw-items-center tw-gap-1.5">
        <span className="tw-inline-block tw-w-1 tw-h-1 tw-rounded-xl tw-bg-primary-500" />
        On this page
      </p>
      <ul className="tw-space-y-0.5 tw-list-none tw-pl-0 tw-mb-0">
        {toc.map(item => {
          const isActive = activeId === item.id;
          const isH3 = item.level === 'H3';
          return (
            <li key={item.id}>
              <HashLink
                to={`/documentation/user-guide/${slug}#${item.id}`}
                smooth
                className={`tw-block tw-py-1 tw-text-[12px] tw-leading-snug tw-transition tw-no-underline tw-rounded-sm
                  ${isActive
                    ? 'tw-text-primary-700 tw-font-semibold tw-border-l-2 tw-border-primary-600 tw-bg-primary-50/60'
                    : 'tw-text-slate-600 hover:tw-text-primary-700 tw-border-l-2 tw-border-transparent'
                  }`}
                style={{ paddingLeft: isH3 ? '1.5rem' : '0.75rem' }}
              >
                {item.text}
              </HashLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
TocSidebar.propTypes = {
  toc: PropTypes.array.isRequired,
  slug: PropTypes.string,
  activeId: PropTypes.string,
};

// ── Main page ─────────────────────────────────────────────────────────────────
const UserGuidePage = () => {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const dispatch    = useDispatch();
  const contentRef  = useRef(null);

  const isAdmin   = useSelector(s => s.profile_user?.data?.is_admin === true);
  const { data: guides, loading } = useSelector(s => s.user_guide);

  const [search, setSearch]         = useState('');
  const [showAdmin, setShowAdmin]   = useState(false);
  const [showStats, setShowStats]   = useState(false);
  const [selected, setSelected]     = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState(null);

  const doFetch = useCallback((q = '') => {
    dispatch(isAdmin ? fetchUserGuides(q) : fetchPublishedGuides(q));
  }, [dispatch, isAdmin]);

  useEffect(() => { doFetch(); }, [doFetch]);

  const debouncedSearch = useMemo(
    () => debounce((q) => doFetch(q), 400),
    [doFetch]
  );

  const handleSearchChange = (val) => {
    setSearch(val);
    debouncedSearch(val);
  };

  const handleClearSearch = () => {
    setSearch('');
    doFetch('');
  };

  // Auto-select guide from URL or first available
  useEffect(() => {
    if (!guides.length) return;
    const target = slug ? guides.find(g => g.slug === slug) : null;
    setSelected(target || guides[0]);
  }, [guides, slug]);

  // Track a view of the user-guide currently being read (per slug, per day).
  useEffect(() => {
    if (!selected?.slug) return;
    dispatch(recordDocView({
      docType: 'user_guide',
      docKey:  selected.slug,
      docTitle: selected.title || selected.slug,
    }));
    // one view per slug: the title is only a label, and an edit to it must not count as a new view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selected?.slug]);

  const handleSelect = (guide) => {
    setSelected(guide);
    setShowAdmin(false);
    setMobileNavOpen(false);
    navigate(`/documentation/user-guide/${guide.slug}`, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Group: { parent: { sub: [guides] } }
  const grouped = useMemo(() =>
    guides.reduce((acc, g) => {
      const parent = extractParent(g.group_name);
      const sub    = extractSub(g.group_name);
      if (!acc[parent]) acc[parent] = {};
      if (!acc[parent][sub]) acc[parent][sub] = [];
      acc[parent][sub].push(g);
      return acc;
    }, {}),
  [guides]);

  // Flattened in display order for prev/next
  const flatGuides = useMemo(() =>
    Object.values(grouped).flatMap(subs => Object.values(subs).flat()),
  [grouped]);

  const currentIndex = selected ? flatGuides.findIndex(g => g.id === selected.id) : -1;
  const prevGuide = currentIndex > 0 ? flatGuides[currentIndex - 1] : null;
  const nextGuide = (currentIndex >= 0 && currentIndex < flatGuides.length - 1)
    ? flatGuides[currentIndex + 1] : null;

  const { html: processedHtml, toc } = useMemo(
    () => processAnchors(selected?.content_html || ''),
    [selected]
  );

  // Scroll-spy: keep TOC in sync with viewport
  useEffect(() => {
    if (!toc.length) {
      setActiveHeadingId(null);
      return undefined;
    }
    setActiveHeadingId(toc[0]?.id || null);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveHeadingId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    const t = setTimeout(() => {
      toc.forEach(item => {
        const el = document.getElementById(item.id);
        if (el) observer.observe(el);
      });
    }, 0);

    return () => {
      clearTimeout(t);
      observer.disconnect();
    };
  }, [toc, processedHtml]);

  // Cmd/Ctrl+K to focus search; Esc to blur
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        document.getElementById('user-guide-search')?.focus();
      } else if (e.key === 'Escape' && document.activeElement?.id === 'user-guide-search') {
        document.activeElement.blur();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Body scroll lock when mobile nav open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
    return undefined;
  }, [mobileNavOpen]);

  const parents = Object.keys(grouped);
  const selectedParent = selected ? extractParent(selected.group_name) : null;
  const selectedSub    = selected ? extractSub(selected.group_name)    : '';
  const selectedColor  = selectedParent ? colorForGroup(selectedParent) : GROUP_PALETTE[0];

  return (
    <div className="tw-min-h-screen user-guide-page" style={{ backgroundColor: '#F1F2F5' }}>
      <style>{`
        .user-guide-content h2,
        .user-guide-content h3,
        .user-guide-content h4 {
          scroll-margin-top: calc(var(--falcon-top-nav-height, 4.3125rem) + 4.75rem);
        }
        /* Type scale: keep a clear hierarchy between section (h2),
           sub-section/step (h3), and minor (h4) headings. Without this the
           Bootstrap defaults render h2 and h3 at nearly the same size. */
        .user-guide-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.3;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .user-guide-content h3 {
          font-size: 1.15rem;
          font-weight: 600;
          line-height: 1.35;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .user-guide-content h4 {
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.4;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .user-guide-content p,
        .user-guide-content li {
          font-size: 0.95rem;
          line-height: 1.65;
        }
        .user-guide-page input#user-guide-search::-webkit-search-cancel-button,
        .user-guide-page input#user-guide-search::-webkit-search-decoration {
          display: none;
        }
        .user-guide-page .user-guide-content blockquote {
          border-left: 3px solid #6366f1;
          background: rgba(238, 242, 255, 0.6);
          padding: 0.75rem 1rem;
          border-radius: 0 0.5rem 0.5rem 0;
          margin: 1rem 0;
        }
        ${contentImageCss('.user-guide-content')}
      `}</style>
      <StickyHeader
        search={search}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        isAdmin={isAdmin}
        showAdmin={showAdmin}
        onToggleAdmin={() => { setShowAdmin(v => !v); setShowStats(false); }}
        showStats={showStats}
        onToggleStats={() => { setShowStats(v => !v); setShowAdmin(false); }}
        selected={selected}
        onToggleMobileNav={() => setMobileNavOpen(v => !v)}
      />

      <div className="tw-max-w-screen-2xl tw-mx-auto tw-px-4">
        {showStats ? (
          /* Statistics mode — full-width per-user/per-day view stats */
          <div className="tw-py-6">
            <DocViewStatsPanel docType="user_guide" title="User Guide — view statistics" />
          </div>
        ) : showAdmin ? (
          /* Admin mode — full-width panel */
          <div className="tw-py-6">
            <UserGuideAdminPanel
              onGuideSelect={handleSelect}
              onGuideUpdated={guide => { setSelected(guide); }}
            />
          </div>
        ) : (
          <div className={`tw-grid tw-grid-cols-1 lg:tw-grid-cols-[240px_minmax(0,1fr)] ${toc.length > 0 ? 'xl:tw-grid-cols-[240px_minmax(0,1fr)_220px]' : ''} tw-gap-x-8`}>

            {/* ── Left sidebar (desktop) ── */}
            <aside
              className="tw-hidden lg:tw-block tw-sticky tw-py-6"
              style={{
                top: 'calc(var(--falcon-top-nav-height, 4.3125rem) + 3.75rem)',
                height: 'max(320px, calc(100dvh - var(--falcon-top-nav-height, 4.3125rem) - 3.75rem))',
              }}
            >
              <div className="tw-h-full tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50 tw-shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] tw-flex tw-flex-col tw-overflow-hidden">
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-200/70 tw-bg-gradient-to-r tw-from-primary-50/70 tw-to-sky-50/60 tw-shrink-0">
                  <p className="tw-text-[10px] tw-font-bold tw-text-primary-700 tw-uppercase tw-tracking-wider tw-mb-0 tw-flex tw-items-center tw-gap-1.5">
                    <FiBookOpen size={11} />
                    Categories
                  </p>
                </div>
                <div className="auto-hide-scroll tw-flex-1 tw-overflow-y-auto tw-p-2">
                  {loading && !parents.length ? (
                    <LoadingSpinner size="sm" />
                  ) : parents.length === 0 ? (
                    <div className="tw-text-center tw-py-8 tw-px-3 tw-rounded tw-border tw-border-dashed tw-border-slate-300 tw-bg-slate-100/60 tw-mt-2">
                      <p className="tw-text-sm tw-text-slate-500 tw-mb-0">
                        {search ? 'No results' : 'No guides yet'}
                      </p>
                    </div>
                  ) : (
                    parents.map(parent => (
                      <TreeGroup
                        key={parent}
                        parent={parent}
                        subGroups={grouped[parent]}
                        selectedSlug={selected?.slug}
                        onSelect={handleSelect}
                        defaultOpen={parent === selectedParent || !!search}
                        isActiveGroup={parent === selectedParent}
                      />
                    ))
                  )}
                </div>
              </div>
            </aside>

            {/* ── Mobile sidebar drawer ── */}
            {mobileNavOpen && (
              <>
                <div
                  className="lg:tw-hidden tw-fixed tw-inset-0 tw-bg-slate-900/50 tw-backdrop-blur-sm tw-z-40"
                  onClick={() => setMobileNavOpen(false)}
                  role="presentation"
                />
                <aside className="auto-hide-scroll lg:tw-hidden tw-fixed tw-top-0 tw-left-0 tw-bottom-0 tw-w-72 tw-bg-slate-50 tw-z-50 tw-overflow-y-auto tw-p-4 tw-shadow-2xl tw-border-r tw-border-slate-200">
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-4 tw-pb-3 tw-border-b tw-border-slate-200">
                    <span className="tw-font-bold tw-text-primary-700 tw-uppercase tw-text-xs tw-tracking-wider">Navigation</span>
                    <button
                      type="button"
                      onClick={() => setMobileNavOpen(false)}
                      className="tw-p-1 tw-text-slate-500 hover:tw-text-rose-600 tw-border-0 tw-bg-transparent tw-rounded-sm tw-transition-colors"
                      aria-label="Close navigation"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                  {parents.length === 0 ? (
                    <p className="tw-text-sm tw-text-slate-500 tw-py-8 tw-text-center">
                      {search ? 'No results' : 'No guides yet'}
                    </p>
                  ) : parents.map(parent => (
                    <TreeGroup
                      key={parent}
                      parent={parent}
                      subGroups={grouped[parent]}
                      selectedSlug={selected?.slug}
                      onSelect={handleSelect}
                      defaultOpen={parent === selectedParent}
                      isActiveGroup={parent === selectedParent}
                    />
                  ))}
                </aside>
              </>
            )}

            {/* ── Main article ── */}
            <main ref={contentRef} className="tw-min-w-0 tw-py-8">
              {loading && !selected ? (
                <LoadingSpinner size="lg" message="Loading guide…" />
              ) : selected ? (
                <article className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50 tw-shadow-[0_4px_20px_-8px_rgba(15,23,42,0.12)] tw-px-6 sm:tw-px-9 tw-py-8">
                  <header className="tw-mb-6 tw-pb-5 tw-border-b tw-border-slate-200">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                          <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-px-2 tw-py-0.5 tw-rounded-md ${selectedColor.countBg}`}>
                            <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-xl ${selectedColor.dot}`} />
                            {stripEmoji(selectedParent)}
                            {selectedSub && (
                              <>
                                <span className="tw-opacity-50">/</span>
                                <span className="tw-normal-case tw-font-semibold">{stripEmoji(selectedSub)}</span>
                              </>
                            )}
                          </span>
                          {!selected.published && (
                            <span className="tw-text-[10px] tw-font-bold tw-text-amber-800 tw-bg-amber-100 tw-border tw-border-amber-200 tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-uppercase">
                              Draft
                            </span>
                          )}
                        </div>
                        <h1 className="tw-text-3xl tw-font-bold tw-text-slate-900 tw-tracking-tight tw-mb-2">
                          {stripEmoji(selected.title)}
                        </h1>
                        {selected.description && (
                          <p className="tw-text-base tw-text-slate-600 tw-mb-0 tw-leading-relaxed">{selected.description}</p>
                        )}
                      </div>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => setShowAdmin(true)}>
                          <FiEdit2 size={12} /> Edit
                        </Button>
                      )}
                    </div>
                  </header>

                  {processedHtml ? (
                    <div
                      className="tw-prose tw-prose-slate tw-max-w-none user-guide-content prose-headings:tw-font-bold prose-headings:tw-text-slate-900 prose-p:tw-text-slate-700 prose-li:tw-text-slate-700 prose-a:tw-text-primary-600 prose-a:tw-font-medium hover:prose-a:tw-text-primary-800 prose-strong:tw-text-slate-900 prose-code:tw-text-rose-700 prose-code:tw-bg-rose-100/60 prose-code:tw-rounded-sm prose-code:tw-px-1 prose-code:tw-font-medium"
                      dangerouslySetInnerHTML={createMarkup(processedHtml)}
                    />
                  ) : (
                    <p className="tw-text-slate-500 tw-text-sm tw-italic tw-text-center tw-py-12">
                      No content yet. {isAdmin && 'Click Edit to add content.'}
                    </p>
                  )}
                </article>
              ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-24 tw-text-slate-500 tw-gap-3 tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-bg-slate-50/60">
                  <div className="tw-p-3 tw-bg-primary-100 tw-rounded-xl">
                    <FiBookOpen size={28} className="tw-text-primary-600" />
                  </div>
                  <p className="tw-text-sm tw-mb-0">Select a guide from the sidebar</p>
                </div>
              )}

              {selected && (prevGuide || nextGuide) && (
                <nav className="tw-mt-6 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                  {prevGuide ? (
                    <button
                      type="button"
                      onClick={() => handleSelect(prevGuide)}
                      className="tw-group tw-flex tw-items-center tw-gap-3 tw-text-left tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 hover:tw-border-primary-400 hover:tw-bg-primary-50/70 hover:tw-shadow-sm tw-transition-all tw-cursor-pointer"
                    >
                      <div className="tw-p-2 tw-bg-slate-200 group-hover:tw-bg-primary-100 tw-rounded tw-shrink-0 tw-transition-colors">
                        <FiArrowLeft className="tw-text-slate-600 group-hover:tw-text-primary-700" />
                      </div>
                      <div className="tw-min-w-0">
                        <p className="tw-text-[10px] tw-font-bold tw-text-slate-500 group-hover:tw-text-primary-600 tw-uppercase tw-tracking-wider tw-mb-0.5">Previous</p>
                        <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate tw-mb-0">{stripEmoji(prevGuide.title)}</p>
                      </div>
                    </button>
                  ) : <span className="tw-hidden sm:tw-block" />}
                  {nextGuide ? (
                    <button
                      type="button"
                      onClick={() => handleSelect(nextGuide)}
                      className="tw-group tw-flex tw-items-center tw-justify-end tw-gap-3 tw-text-right tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 hover:tw-border-primary-400 hover:tw-bg-primary-50/70 hover:tw-shadow-sm tw-transition-all tw-cursor-pointer"
                    >
                      <div className="tw-min-w-0">
                        <p className="tw-text-[10px] tw-font-bold tw-text-slate-500 group-hover:tw-text-primary-600 tw-uppercase tw-tracking-wider tw-mb-0.5">Next</p>
                        <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate tw-mb-0">{stripEmoji(nextGuide.title)}</p>
                      </div>
                      <div className="tw-p-2 tw-bg-slate-200 group-hover:tw-bg-primary-100 tw-rounded tw-shrink-0 tw-transition-colors">
                        <FiArrowRight className="tw-text-slate-600 group-hover:tw-text-primary-700" />
                      </div>
                    </button>
                  ) : <span className="tw-hidden sm:tw-block" />}
                </nav>
              )}

              {selected && (
                <div className="tw-mt-6 tw-mb-8 tw-rounded-lg tw-bg-gradient-to-r tw-from-primary-100 tw-via-primary-50 tw-to-sky-50 tw-border tw-border-primary-200 tw-p-4 tw-flex tw-items-center tw-gap-3">
                  <div className="tw-p-2 tw-bg-primary-200/60 tw-rounded tw-shrink-0">
                    <FiHelpCircle className="tw-text-primary-700" size={18} />
                  </div>
                  <p className="tw-text-sm tw-text-slate-700 tw-mb-0">
                    Have a question or feedback? Contact <strong className="tw-text-primary-800">IT Department</strong>.
                  </p>
                </div>
              )}
            </main>

            {/* ── Right TOC (xl+, only when anchors exist) ── */}
            {toc.length > 0 && (
              <aside
                className="tw-hidden xl:tw-block tw-sticky tw-py-8"
                style={{
                  top: 'calc(var(--falcon-top-nav-height, 4.3125rem) + 3.5rem)',
                  height: 'max(320px, calc(100dvh - var(--falcon-top-nav-height, 4.3125rem) - 3.5rem))',
                }}
              >
                <div className="auto-hide-scroll tw-h-full tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50 tw-shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] tw-overflow-y-auto tw-p-2">
                  <TocSidebar toc={toc} slug={selected?.slug} activeId={activeHeadingId} />
                </div>
              </aside>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default UserGuidePage;
